import { ReactElement, useContext, useState } from 'react';

import { Field } from 'formik';
import gridStyle from '@pages/forms/Grid.module.scss';
import { Alert, Button, Modal, TextInput } from '@components';
import VicFinesContext from '@pages/forms/VicFinesFormContext';
import InfoIcon from '../../../../images/info.svg';
import QuestionIcon from '../../../../images/question.svg';
// import mailRef from '../../../../images/refNo_mail.jpg';
import styles from './InfringementExtStep1.module.scss';

const isInfringementIneligibleCode = 'isInfringementIneligible'; // Error Message 1
const isPersonUnaware = 'isPersonUnaware'; // Error Message 2
const isIneligibleCode = 'isIneligible'; // Error Message 3
const isNominatedCode = 'isNominated'; // Error Message 4
const isFullyPaidCode = 'isFullyPaid'; // Error Message 5

function InfringementExtStep1(): ReactElement {
  const { apiError, tooltips } = useContext(VicFinesContext);
  const validTitle = Object.keys(tooltips).filter((v) => v.includes('obligationlocationonformstep1modal'));
  const modalContent = tooltips[validTitle[0]];
  const titleText = validTitle[0].substring(validTitle[0].indexOf('|'), validTitle[0].length).replaceAll('|', ' ');

  const navigateToPayFines = () => {
    window.location.href = 'https://online.fines.vic.gov.au/Your-options/Pay-your-fine';
  };
  const navigateToViewFines = () => {
    window.location.href = '/Your-options/Your-Fines';
  };
  // Error Code 1
  const MagistratesCourtIneligibleAlertContentError1 = (): ReactElement => (
    <>
      <strong>You are not eligible to apply for an infringement extension through Fines Victoria.</strong>
      <p>
        You may be eligible to apply for an infringement extension instead through the
        {' '}
        <a href="https://www.mcv.vic.gov.au/criminal-matters/driving-and-traffic-offences/applying-infringement-extension" title="Magistrates Court Link">
          Magistrates' Court.
        </a>
      </p>
      <br />
      <p>
        If you would like to speak to someone, call Fines Victoria on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          (03) 9200 8111
        </a>
        {' '}
        or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        (regional callers).
      </p>
      <p>We are available Monday to Friday, 8am to 6pm (except public holidays).</p>
      <br />
      <p>If you do not deal with your outstanding fines, enforcement action may continue.</p>

      <div className={styles.errorMessageButtons}>
        <Button onClick={navigateToViewFines} color="blue" size="medium">
          View your fines
        </Button>
      </div>
    </>
  );

  // Error Code 2
  const PersonUnawareIneligibleAlertContentError2 = (): ReactElement => (
    <>
      <strong>You are not eligible to apply for an infringement extension.</strong>
      <p>
        You may be eligible to
        {' '}
        <a href="https://online.fines.vic.gov.au/Your-options/Request-a-review" title="Magistrates Court Link">
          request a review
        </a>
        {' '}
        on the grounds of person unaware.
      </p>
      <br />
      <p>
        If you would like to speak to someone, call Fines Victoria on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          (03) 9200 8111
        </a>
        {' '}
        or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        (regional callers).
      </p>
      <p>We are available Monday to Friday, 8am to 6pm (except public holidays).</p>
      <br />
      <p>If you do not deal with your outstanding fines, enforcement action may continue.</p>

      <div className={styles.errorMessageButtons}>
        <Button onClick={navigateToViewFines} color="blue" size="medium">
          View your fines
        </Button>
      </div>
    </>
  );

  // Error Code 3
  const IneligibleObligationAlertContentError3 = (): ReactElement => (
    <>
      <strong>You are not eligible to apply for an infringement extension.</strong>
      <p>
        If you would like to speak to someone, call Fines Victoria on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          (03) 9200 8111
        </a>
        {' '}
        or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        (regional callers).
      </p>
      <p>We are available Monday to Friday, 8am to 6pm (except public holidays).</p>
      <br />
      <p>Pay now to avoid additional costs.</p>

      <div className={styles.errorMessageButtons}>
        <Button onClick={navigateToPayFines} color="blue" size="small_medium">
          Pay your fines
        </Button>
        <Button onClick={navigateToViewFines} color="blue" size="small_medium">
          View your fines
        </Button>
      </div>
    </>
  );

  // Error Code 4
  const NominatedIneligibleAlertContentError4 = (): ReactElement => (
    <p>The obligation you have entered may be closed or may have been nominated to a third party. Check that it is correct and try again.</p>
  );

  // Error Code 5
  const PaidIneligibleAlertContentError5 = (): ReactElement => (
    <>
      <p>The obligation you have entered may be closed or may have been nominated to a third party. Check that it is correct and try again.</p>
      <br />
      <p>
        If you have just become aware of your fine, call Fines Victoria on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          (03) 9200 8111
        </a>
        {' '}
        or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        (for regional callers).
      </p>
    </>
  );

  // For Any other error
  const InfringementExtStep1AlertContent = (): ReactElement => <p>{`${apiError?.Error}`}</p>;

  const [isOpenHelp, setOpenHelp] = useState(false);
  // const [smallScreen, setMatches] = useState(window.matchMedia('(max-width: 1150px)').matches);

  const closeNotifications = () => {
    setOpenHelp(false);
  };

  const InfringementExtStep1NoteContent = (): ReactElement => (
    // TODO - Connect to Sitecore to make this content manageable.
    <>
      <p>You will need to enter the fine's obligation number, the offence date and some other details about yourself or your vehicle.</p>
      <br />
      <span>
        If you don't have these details please contact us on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          03 9200 8111
        </a>
        {' '}
        or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        (regional callers).
      </span>
    </>
  );

  const notificationInformation = (): ReactElement => (
    // eslint-disable-next-line react/no-danger
    <div className={styles.accordContent} dangerouslySetInnerHTML={{ __html: modalContent }} />
  );

  return (
    <>
      <div className={gridStyle.form}>
        <div className="vfH2">Your fine</div>
        <span className="subHeadingH3">
          At this stage of the application, you only need to provide the details of one of your fines - even if you are dealing with more than one fine.
        </span>
        {apiError?.Error === isInfringementIneligibleCode && <Alert icon={InfoIcon} message={<MagistratesCourtIneligibleAlertContentError1 />} theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isPersonUnaware && <Alert icon={InfoIcon} message={<PersonUnawareIneligibleAlertContentError2 />} theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isIneligibleCode && <Alert icon={InfoIcon} message={<IneligibleObligationAlertContentError3 />} theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isNominatedCode && <Alert icon={InfoIcon} message={<NominatedIneligibleAlertContentError4 />} theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isFullyPaidCode && <Alert icon={InfoIcon} message={<PaidIneligibleAlertContentError5 />} theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error
        && apiError?.Error !== isIneligibleCode
        && apiError?.Error !== isNominatedCode
        && apiError?.Error !== isInfringementIneligibleCode
        && apiError?.Error !== isFullyPaidCode
        && apiError?.Error !== isPersonUnaware
        && <Alert icon={InfoIcon} message={<InfringementExtStep1AlertContent />} theme="error" classAlt={styles.boxheight} />}
        <Field
          name="OBLIGATION_NUMBER"
          component={TextInput}
          title="Obligation number"
          icon={QuestionIcon}
          iconPosition="end"
          iconClick={() => setOpenHelp(true)}
          isMandatory
          autocomplete="off"
        />
        {isOpenHelp && <Modal show close={closeNotifications} title={titleText} content={notificationInformation()} />}
      </div>
      <Alert message={<InfringementExtStep1NoteContent />} theme="info" icon={InfoIcon} />
    </>
  );
}

export default InfringementExtStep1;
